import React, { useState, useCallback, useEffect } from "react";
import { TourOverlay } from "./TourOverlay";
import { TourContext } from "./context";

// Re-export for backwards compatibility with existing importers.
export { useTour } from "./context";

/* -----------------------------------------------------------------------------
 * TourProvider — global state + persistence for the interactive onboarding.
 *
 * The tour steps live in DOM (attached via data-testid selectors); we only
 * track the *current step number* (0 = inactive, 1..6 = active).
 * State is mirrored to localStorage so the tour survives page reloads and
 * navigation. Consumers use `useTour()` to read/mutate state.
 * --------------------------------------------------------------------------- */
const KEY_STEP = "ag7_tour_step";
const KEY_ACTIVE = "ag7_tour_active";
const KEY_OFFERED = "ag7_tour_offered_v1"; // whether the welcome modal already offered the tour

// Upper bound for the tour step counter. Must match TERMINAL_STEP in
// TourOverlay.jsx (STEP_DEFS.length - 1). Kept as a plain constant here so
// this file doesn't have to import STEP_DEFS and re-create the circular
// dependency we just fixed.
const MAX_STEP = 13;

export function TourProvider({ children }) {
  const [step, setStep] = useState(() => {
    try { return parseInt(localStorage.getItem(KEY_STEP) || "0", 10); }
    catch (e) { console.warn("TourProvider: localStorage read (step) failed:", e); return 0; }
  });
  const [active, setActive] = useState(() => {
    try { return localStorage.getItem(KEY_ACTIVE) === "1"; }
    catch (e) { console.warn("TourProvider: localStorage read (active) failed:", e); return false; }
  });

  useEffect(() => {
    try {
      localStorage.setItem(KEY_STEP, String(step));
      localStorage.setItem(KEY_ACTIVE, active ? "1" : "0");
    } catch (e) {
      // private-browsing safe — persistence not critical, but log for observability.
      console.warn("TourProvider: localStorage write failed:", e);
    }
  }, [step, active]);

  const start = useCallback(() => { setStep(1); setActive(true); }, []);
  const next = useCallback(() => { setStep((s) => (s < MAX_STEP ? s + 1 : MAX_STEP)); }, []);
  const back = useCallback(() => { setStep((s) => (s > 1 ? s - 1 : 1)); }, []);
  const goto = useCallback((n) => {
    if (typeof n !== "number") return;
    const clamped = Math.max(0, Math.min(MAX_STEP, n));
    setStep(clamped);
    setActive(clamped > 0);
  }, []);
  const skip = useCallback(() => { setActive(false); setStep(0); }, []);
  const finish = useCallback(() => { setActive(false); setStep(0); }, []);

  const markOffered = useCallback(() => {
    try { localStorage.setItem(KEY_OFFERED, "1"); }
    catch (e) { console.warn("TourProvider: markOffered failed:", e); }
  }, []);
  const wasOffered = useCallback(() => {
    try { return localStorage.getItem(KEY_OFFERED) === "1"; }
    catch (e) { console.warn("TourProvider: wasOffered read failed:", e); return false; }
  }, []);

  return (
    <TourContext.Provider value={{ step, active, start, next, back, goto, skip, finish, markOffered, wasOffered }}>
      {children}
      <TourOverlay />
    </TourContext.Provider>
  );
}
